import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { takeUntil, switchMap, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { State } from 'src/app/store';
import { UserService } from '../../service/user.abstract.service';
import { userRecordSelector } from '../../store/user.selectors';
import { UserActivityItem, UserActivityResponse, UserRecord } from '../../user.model';

@Component({
    selector: 'app-user-history',
    templateUrl: './user-history.component.html',
    styleUrls: ['./user-history.component.scss'],
})
export class UserHistoryComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    public userActivity: UserActivityItem[] = [];

    public userRecord: UserRecord | null = null;

    public loading = false;

    public error = false;

    constructor(
        private store: Store<State>,
        private userService: UserService,
        private translateService: TranslateService,
    ) {}

    public ngOnInit(): void {
        this.loadUserActivity();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadUserActivity(): void {
        this.loading = true;
        this.error = false;

        this.store
            .select(userRecordSelector)
            .pipe(
                takeUntil(this.destroy$),
                switchMap((userRecord) => {
                    this.userRecord = userRecord;
                    if (userRecord?.id) {
                        return this.userService.getUserActivity(userRecord.id);
                    }
                    return of({ user: null, activities: [] } as UserActivityResponse);
                }),
                catchError(() => {
                    this.error = true;
                    return of({ user: null, activities: [] } as UserActivityResponse);
                }),
            )
            .subscribe({
                next: (userActivity) => {
                    if (userActivity?.user) {
                        this.userActivity = userActivity?.activities?.slice().sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                        this.loading = false;
                    }
                },
                error: () => {
                    this.error = true;
                    this.loading = false;
                },
            });
    }

    public formatTime(dateString: string): string {
        const date = new Date(dateString);
        const { currentLang } = this.translateService;

        if (currentLang === 'fr') {
            return date.toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
        }

        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    }

    public formatDate(dateString: string): string {
        const date = new Date(dateString);
        const { currentLang } = this.translateService;

        if (currentLang === 'fr') {
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        }

        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        });
    }

    public getActivityDescription(activityType: UserActivityItem): string {
        const translationKey = `pages.user-update.historyDescriptions.${activityType.metadata.type}`;

        const user = {
            _id: this.userRecord.id,
            firstName: this.userRecord.firstName,
            lastName: this.userRecord.lastName,
        };

        const translation = this.translateService.instant(translationKey, {
            ...activityType.metadata,
            ...(activityType.metadata.familyLinkType ? { familyLinkType: `"${this.translateService.instant(`pages.user-cards.family.common.link.${activityType.metadata.familyLinkType}`)}"` } : {}),
            ...(activityType.metadata.admin ? { admin: this.generateClickableName(activityType.metadata.admin, '#66c40b') } : {}),
            ...(activityType.metadata.familyMember
                ? { familyMember: this.generateClickableName(activityType.metadata.familyMember, this.getUserColor(user, activityType.metadata.familyMember)) }
                : {}),
            ...(activityType.metadata.inheritedFrom ? { inheritedFrom: this.generateClickableName(activityType.metadata.inheritedFrom, '#f6a513') } : {}),
            ...(activityType.metadata.initiator ? { initiator: this.generateClickableName(activityType.metadata.initiator, this.getUserColor(user, activityType.metadata.initiator)) } : {}),
            user: this.generateClickableName(user, '#55b4d7'),
        });

        // If translation key is not found, return the key itself as fallback
        return translation === translationKey ? 'Unknown activity' : translation;
    }

    private generateClickableName(user: { _id: string; firstName: string; lastName: string }, color: string): string {
        // Need to hardcode color because of <innerHtml>
        return `<a style="color: ${color}; font-weight: bold;" href="${window.location.origin}/user-update/${user._id}" target="_blank">${user.firstName} ${user.lastName?.charAt(0) || ''}.</a>`;
    }

    private getUserColor(user: { _id: string; firstName: string; lastName: string }, initiator: { _id: string; firstName: string; lastName: string }): string {
        return initiator._id === user._id ? '#55b4d7' : '#f6a513';
    }
}
